import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import DashboardLayout from '@/components/layout/DashboardLayout';
import {
  Loader2, Percent, Globe, Phone, Mail, MapPin, Image, Star, Trash2, Plus,
  MessageSquare, Eye, Pencil, Save, Facebook, Twitter, Instagram, Linkedin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import { adminAPI } from '@/services/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

// ────────────────────────────────────────────────────────────────
// Tab: Général (commission + branding)
// ────────────────────────────────────────────────────────────────
const GeneralTab = ({ settings, onSaved }) => {
  const [form, setForm] = useState({
    default_commission_percentage: '',
    platform_name: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm({
      default_commission_percentage:
        settings.default_commission_percentage != null
          ? String(settings.default_commission_percentage)
          : '',
      platform_name: settings.platform_name || '',
    });
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = new FormData();
      data.append('platform_name', form.platform_name);
      const pct = form.default_commission_percentage.trim();
      if (pct === '') {
        data.append('default_commission_percentage', '');
      } else {
        const num = parseFloat(pct);
        if (isNaN(num) || num < 0 || num > 100) {
          toast({ title: 'Erreur', description: 'Commission entre 0 et 100.', variant: 'destructive' });
          return;
        }
        data.append('default_commission_percentage', num);
      }
      if (logoFile) data.append('platform_logo', logoFile);
      await adminAPI.updateAdminSettings(data);
      toast({ title: 'Enregistré', description: 'Paramètres généraux mis à jour.' });
      onSaved?.();
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div className="space-y-2">
        <Label>Nom de la plateforme</Label>
        <Input value={form.platform_name} onChange={(e) => setForm((p) => ({ ...p, platform_name: e.target.value }))} />
      </div>
      <div className="space-y-2">
        <Label>Logo de la plateforme</Label>
        {settings?.platform_logo_url && (
          <img src={settings.platform_logo_url} alt="Logo" className="h-16 w-auto rounded mb-2 border" />
        )}
        <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
      </div>
      <div className="space-y-2">
        <Label>Commission par défaut (%)</Label>
        <Input
          type="number" step="0.01" min="0" max="100" placeholder="Ex: 10"
          value={form.default_commission_percentage}
          onChange={(e) => setForm((p) => ({ ...p, default_commission_percentage: e.target.value }))}
        />
        <p className="text-xs text-muted-foreground">Laissez vide pour ne pas définir de pourcentage par défaut.</p>
      </div>
      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Enregistrer
      </Button>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Tab: Contact
// ────────────────────────────────────────────────────────────────
const ContactTab = ({ settings, onSaved }) => {
  const [form, setForm] = useState({ contact_email: '', contact_phone: '', contact_address: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm({
      contact_email: settings.contact_email || '',
      contact_phone: settings.contact_phone || '',
      contact_address: settings.contact_address || '',
    });
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateAdminSettings(form);
      toast({ title: 'Enregistré', description: 'Coordonnées mises à jour.' });
      onSaved?.();
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div className="space-y-2">
        <Label>Email de contact</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-10" value={form.contact_email} onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Téléphone</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-10" value={form.contact_phone} onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Adresse</Label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-10" value={form.contact_address} onChange={(e) => setForm((p) => ({ ...p, contact_address: e.target.value }))} />
        </div>
      </div>
      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Enregistrer
      </Button>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Tab: Réseaux sociaux
// ────────────────────────────────────────────────────────────────
const SocialTab = ({ settings, onSaved }) => {
  const [form, setForm] = useState({ facebook_url: '', twitter_url: '', instagram_url: '', linkedin_url: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setForm({
      facebook_url: settings.facebook_url || '',
      twitter_url: settings.twitter_url || '',
      instagram_url: settings.instagram_url || '',
      linkedin_url: settings.linkedin_url || '',
    });
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateAdminSettings(form);
      toast({ title: 'Enregistré', description: 'Réseaux sociaux mis à jour.' });
      onSaved?.();
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'facebook_url', icon: Facebook, label: 'Facebook' },
    { key: 'twitter_url', icon: Twitter, label: 'Twitter / X' },
    { key: 'instagram_url', icon: Instagram, label: 'Instagram' },
    { key: 'linkedin_url', icon: Linkedin, label: 'LinkedIn' },
  ];

  return (
    <div className="space-y-6 max-w-xl">
      {fields.map((f) => (
        <div key={f.key} className="space-y-2">
          <Label>{f.label}</Label>
          <div className="relative">
            <f.icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-10" placeholder={`https://${f.label.toLowerCase()}.com/...`}
              value={form[f.key]}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            />
          </div>
        </div>
      ))}
      <Button onClick={handleSave} disabled={saving}>
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Enregistrer
      </Button>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Tab: Témoignages (CRUD)
// ────────────────────────────────────────────────────────────────
const TestimonialsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ author_name: '', author_role: '', content: '', rating: '5', order: '0', is_active: true });
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await adminAPI.getTestimonials();
      setItems(res.data?.results ?? res.data ?? []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ author_name: '', author_role: '', content: '', rating: '5', order: '0', is_active: true });
    setAvatarFile(null);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      author_name: item.author_name,
      author_role: item.author_role || '',
      content: item.content,
      rating: String(item.rating),
      order: String(item.order),
      is_active: item.is_active,
    });
    setAvatarFile(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.author_name.trim() || !form.content.trim()) {
      toast({ title: 'Erreur', description: 'Nom et contenu sont requis.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const data = new FormData();
      data.append('author_name', form.author_name);
      data.append('author_role', form.author_role);
      data.append('content', form.content);
      data.append('rating', form.rating);
      data.append('order', form.order);
      data.append('is_active', form.is_active);
      if (avatarFile) data.append('author_avatar', avatarFile);

      if (editItem) {
        await adminAPI.updateTestimonial(editItem.id, data);
      } else {
        await adminAPI.createTestimonial(data);
      }
      toast({ title: 'Enregistré' });
      setDialogOpen(false);
      loadData();
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce témoignage ?')) return;
    try {
      await adminAPI.deleteTestimonial(id);
      toast({ title: 'Supprimé' });
      loadData();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} témoignage(s)</p>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Ajouter</Button>
      </div>

      {items.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">Aucun témoignage pour le moment.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <Card key={t.id} className={`${!t.is_active ? 'opacity-50' : ''}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-3">
                  {t.author_avatar_url ? (
                    <img src={t.author_avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-bold text-sm">
                      {(t.author_name || '?')[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{t.author_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.author_role}</p>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(t.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-3">"{t.content}"</p>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {t.is_active ? 'Actif' : 'Inactif'}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(t.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Modifier le témoignage' : 'Nouveau témoignage'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Nom *</Label>
                <Input value={form.author_name} onChange={(e) => setForm((p) => ({ ...p, author_name: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Rôle / Poste</Label>
                <Input value={form.author_role} onChange={(e) => setForm((p) => ({ ...p, author_role: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Contenu *</Label>
              <textarea
                rows={3} value={form.content}
                onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Note (1-5)</Label>
                <Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm((p) => ({ ...p, rating: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Ordre</Label>
                <Input type="number" min="0" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Statut</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.is_active ? 'true' : 'false'}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.value === 'true' }))}
                >
                  <option value="true">Actif</option>
                  <option value="false">Inactif</option>
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Avatar</Label>
              <Input type="file" accept="image/*" onChange={(e) => setAvatarFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editItem ? 'Modifier' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Tab: Messages de contact
// ────────────────────────────────────────────────────────────────
const ContactMessagesTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await adminAPI.getContactMessages();
      setMessages(res.data?.results ?? res.data ?? []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const markAsRead = async (msg) => {
    try {
      await adminAPI.updateContactMessage(msg.id, { is_read: true });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, is_read: true } : m)));
      if (selected?.id === msg.id) setSelected((p) => ({ ...p, is_read: true }));
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {messages.length} message(s) — {unreadCount} non lu(s)
      </p>

      {messages.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">Aucun message de contact.</p>
      ) : (
        <div className="divide-y border rounded-xl overflow-hidden">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors flex items-start gap-4 ${!m.is_read ? 'bg-blue-50/50' : ''}`}
              onClick={() => { setSelected(m); if (!m.is_read) markAsRead(m); }}
            >
              <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${!m.is_read ? 'bg-blue-500' : 'bg-transparent'}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm truncate ${!m.is_read ? 'font-semibold' : ''}`}>{m.subject}</p>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(m.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{m.full_name} — {m.email}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selected?.subject}</DialogTitle>
            <DialogDescription>
              De {selected?.full_name} ({selected?.email})
              {selected?.phone ? ` — ${selected.phone}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm whitespace-pre-wrap text-foreground">{selected?.message}</p>
          </div>
          <DialogFooter>
            <p className="text-xs text-muted-foreground mr-auto">
              {selected?.created_at && new Date(selected.created_at).toLocaleString('fr-FR')}
            </p>
            <Button variant="outline" onClick={() => setSelected(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────
const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      const res = await adminAPI.getAdminSettings();
      setSettings(res.data);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les paramètres.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  return (
    <>
      <Helmet><title>Configuration — LogiConnect</title></Helmet>
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Configuration</h1>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : (
          <Tabs defaultValue="general" className="space-y-6">
            <TabsList className="flex flex-wrap gap-1">
              <TabsTrigger value="general" className="gap-1.5"><Percent className="w-4 h-4" />Général</TabsTrigger>
              <TabsTrigger value="contact" className="gap-1.5"><Phone className="w-4 h-4" />Contacts</TabsTrigger>
              <TabsTrigger value="social" className="gap-1.5"><Globe className="w-4 h-4" />Réseaux</TabsTrigger>
              <TabsTrigger value="testimonials" className="gap-1.5"><Star className="w-4 h-4" />Témoignages</TabsTrigger>
              <TabsTrigger value="messages" className="gap-1.5"><MessageSquare className="w-4 h-4" />Messages</TabsTrigger>
            </TabsList>

            <TabsContent value="general">
              <GeneralTab settings={settings} onSaved={loadSettings} />
            </TabsContent>
            <TabsContent value="contact">
              <ContactTab settings={settings} onSaved={loadSettings} />
            </TabsContent>
            <TabsContent value="social">
              <SocialTab settings={settings} onSaved={loadSettings} />
            </TabsContent>
            <TabsContent value="testimonials">
              <TestimonialsTab />
            </TabsContent>
            <TabsContent value="messages">
              <ContactMessagesTab />
            </TabsContent>
          </Tabs>
        )}
      </DashboardLayout>
    </>
  );
};

export default AdminSettings;
